import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ImageUpload from "../../common/ImageUpload";

export default function CampaignModal({
  show,
  onClose,
  onSubmit,
  initialData,
  isLoading,
}) {
  const [formData, setFormData] = useState({
    title: "",
    applyBy: "",
    typeTags: "",
    location: "",
    description: "",
    link: "",
    imageUrl: "",
    isPopular: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      if (initialData) {
        setFormData({
          title: initialData.title || "",
          applyBy: initialData.applyBy || "",
          location: initialData.location || "",
          description: initialData.description || "",
          link: initialData.link || "",
          imageUrl: initialData.imageUrl || "",
          isPopular: initialData.isPopular ?? false,
          typeTags: Array.isArray(initialData.typeTags)
            ? initialData.typeTags.join(", ")
            : initialData.typeTags || "",
        });
      } else {
        setFormData({
          title: "",
          applyBy: "",
          typeTags: "",
          location: "",
          description: "",
          link: "",
          imageUrl: "",
          isPopular: false,
        });
      }
      setErrors({});
    }
  }, [show, initialData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required.";
    if (!formData.location.trim()) newErrors.location = "Location is required.";
    if (!formData.applyBy.trim())
      newErrors.applyBy = "Apply By date is required.";
    if (!formData.link.trim()) newErrors.link = "External link is required.";
    if (!formData.description.trim())
      newErrors.description = "Description is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        ...formData,
        typeTags: formData.typeTags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag !== ""),
      };
      onSubmit(payload);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          {initialData ? "Edit Campaign" : "Add Campaign"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body className="p-4">
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label className="fw-bold">
                Title <span className="text-danger">*</span>
              </Form.Label>
              {/* CHANGED TO TEXTAREA HERE */}
              <Form.Control
                as="textarea"
                rows={3}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                isInvalid={!!errors.title}
                placeholder="Campaign Title"
                style={{ resize: "none" }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.title}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-6">
              <Form.Label className="fw-bold">
                Location <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                isInvalid={!!errors.location}
                placeholder="e.g. Remote, New York"
              />
              <Form.Control.Feedback type="invalid">
                {errors.location}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-6">
              <Form.Label className="fw-bold">
                Apply By (Date) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="applyBy"
                value={formData.applyBy}
                onChange={handleInputChange}
                isInvalid={!!errors.applyBy}
                placeholder="e.g. 2024-12-31"
              />
              <Form.Control.Feedback type="invalid">
                {errors.applyBy}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-6">
              <Form.Label className="fw-bold">
                External Link <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                isInvalid={!!errors.link}
                placeholder="https://..."
              />
              <Form.Control.Feedback type="invalid">
                {errors.link}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-12 mb-3">
              <ImageUpload
                label="Campaign Image"
                value={formData.imageUrl}
                uploadEndpoint="/upload/pledgeimage"
                error={errors.imageUrl}
                onChange={(newUrl) => {
                  setFormData((prev) => ({ ...prev, imageUrl: newUrl }));
                  if (errors.imageUrl) setErrors((prev) => ({ ...prev, imageUrl: null }));
                }}
              />
            </div>

            <div className="col-md-12">
              <Form.Label className="fw-bold">
                Tags{" "}
                <small className="text-muted fw-normal">
                  (Comma separated)
                </small>
              </Form.Label>
              <Form.Control
                type="text"
                name="typeTags"
                value={formData.typeTags}
                onChange={handleInputChange}
                placeholder="Environment, Remote, Paid"
              />
            </div>

            <div className="col-md-12">
              <Form.Label className="fw-bold">
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!errors.description}
                placeholder="Enter campaign details..."
              />
              <Form.Control.Feedback type="invalid">
                {errors.description}
              </Form.Control.Feedback>
            </div>

            <div className="col-md-12 mt-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="popular-campaign-check"
                  name="isPopular"
                  checked={formData.isPopular}
                  onChange={handleInputChange}
                  style={{ cursor: "pointer" }}
                />
                <label className="form-check-label fw-bold text-black" htmlFor="popular-campaign-check" style={{ cursor: "pointer" }}>
                  Mark as Popular Campaign?
                </label>
              </div>
            </div>

          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                {initialData ? "Update Campaign" : "Create Campaign"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}